/**
 * Directives for module mwApp.auth
 */

/**
 * compareTo directive <br>
 * to compare passwords
 *
 * @class compareTo
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc directive
 * @name mwApp/auth.directive:compareTo
 *
 * @example
 * <form name="form">
 *     Password: <input type="password" name="password" ng-model="password"><br>
 *     Repeat password: <input type="password" name="passwordAgain" compare-to="password" ng-model="passwordAgain">
 *     <div class="text-danger" ng-messages="form.passwordAgain.$error" ng-show="form.passwordAgain.$touched">
 *         <p ng-message="compareTo">Must match the password</p>
 *     </div>
 * </form>
 */
export function compareTo () {
  return {
    restrict: 'A',
    require: 'ngModel',
    scope: {
      otherModelValue: '=compareTo'
    },
    link: function (scope, _element, attributes, ngModel) {
      var equality = attributes.compareToEquality || '='
      var nullable = attributes.compareToNullable === 'yes'

      ngModel.$validators.compareTo = function (modelValue) {
        var v1 = modelValue
        var v2 = scope.otherModelValue

        if (nullable) {
          if (!v1) {
            v1 = null
          }
          if (!v2) {
            v2 = null
          }
        }

        switch (equality) {
          case '=':
          case '==':
          case '===':
          case 'eq':
            return v1 === v2
          case '!=':
          case '!==':
          case '<>':
          case 'ne':
            return v1 !== v2
          case '>':
          case 'gt':
            return v1 > v2
          case '>=':
          case 'ge':
            return v1 >= v2
          case '<':
          case 'lt':
            return v1 < v2
          case '<=':
          case 'le':
            return v1 <= v2
        }
        return false
      }

      scope.$watch('otherModelValue', function () {
        ngModel.$validate()
      })
    }
  }
}

/**
 * passwordValidator directive <br>
 * to validate password
 *
 * @class passwordValidator
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc directive
 * @name mwApp/auth.directive:passwordValidator
 *
 * @example
 * <form name="form">
 *     Password: <input type="password" name="password" ng-model="password"><br>
 *     Repeat password: <input type="password" name="passwordAgain" password-validator ng-model="passwordAgain">
 *     <div class="text-danger" ng-messages="form.passwordAgain.$error" ng-show="form.passwordAgain.$touched">
 *         <p ng-message="pwMaxLength">Password is too long, maximum length is 64 characters</p>
 *     </div>
 * </form>
 */
export function passwordValidator () {
  return {
    restrict: 'A',
    require: 'ngModel',
    link: function (_scope, _element, attributes, ngModel) {
      let condition = true
      if (attributes.passwordValidator === 'false') {
        condition = false
      }
      const noAtLeast = attributes.passwordValidatorNoAtLeast !== undefined

      if (condition) {
        ngModel.$validators.pwMaxLength = (modelValue) =>
          !modelValue ||
          modelValue.length <= 64

        ngModel.$validators.pwMinLength = (modelValue) =>
          !modelValue ||
          modelValue.length >= 6

        ngModel.$validators.pwAtLeast = (modelValue) =>
          noAtLeast ||
          !modelValue ||
          /(?=.*[A-Z])(?=.*\d).*/.test(modelValue)

        ngModel.$validators.pwPattern = (modelValue) =>
          !modelValue ||
          /^[0-9a-zA-Z \Q!"#$%&'()*+,-.:;<=>?@[\]^_`{|}~\E]+$/.test(modelValue)
      }
    }
  }
}

/**
 * access directive <br>
 * checks access string against user rules and deletes elements, if user hasn't rights
 *
 * @class access
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc directive
 * @name mwApp/auth.directive:access
 *
 * @example
 * <button class="btn btn-xs btn-danger" ng-click="vm.deleteCatchup(cu)" access="deleteCatchup">...</button>
 */
/* @ngInject */
export function access (authSvc) {
  return {
    restrict: 'A',
    link: function (scope, element, attrs) {
      var access = attrs.access
      if (access && !authSvc.hasAccess(access, scope)) {
        element.addClass('hidden') // removing not always working, for example inside ngRepeat...
        element.remove()
      }
    }
  }
}

/**
 * Remove rendered feature-flag block when the flag is off for the current user.
 * Exported for focused unit tests (directive teardown paths).
 *
 * @param {angular.$animate} $animate
 * @param {{ block?: { clone: JQLite }, childScope?: angular.IScope, previousElements?: JQLite }} ctx
 */
export function teardownFeatureFlagRendering ($animate, ctx) {
  if (ctx.previousElements) {
    ctx.previousElements.remove()
    ctx.previousElements = null
  }
  if (ctx.childScope) {
    ctx.childScope.$destroy()
    ctx.childScope = null
  }
  if (ctx.block) {
    ctx.previousElements = ctx.block.clone
    $animate.leave(ctx.previousElements).done(function (response) {
      if (response !== false) ctx.previousElements = null
    })
    ctx.block = null
  }
}

/**
 * feature flag directive
 *
 * @class featureFlags
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc directive
 * @name mwApp/auth.directive:featureFlags
 *
 * @example
 * <button feature-flags="FLAG" >...</button>
 */
/* @ngInject */
export function featureFlags ($animate, authSvc) {
  return {
    multiElement: true,
    restrict: 'A',
    transclude: 'element',
    priority: 600,
    terminal: true,
    $$tlb: true,
    link: function ($scope, $element, $attr, ctrl, $transclude) {
      const ctx = {
        block: null,
        childScope: null,
        previousElements: null
      }
      $scope.$watch(function () {
        return $attr.featureFlags
      }, function (value) {
        if (value && !authSvc.hasEnabledFlag(value)) {
          teardownFeatureFlagRendering($animate, ctx)
        } else {
          if (!ctx.childScope) {
            $transclude(function (clone, newScope) {
              ctx.childScope = newScope
              ctx.block = {
                clone: clone
              }
              $animate.enter(clone, $element.parent(), $element)
            })
          }
        }
      })
    }
  }
}
